import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./Beverage.module.css";
import { useLayoutStore, useCampaignPage } from "~/store";
import {
  SignInDialogUi,
  DialogUi,
  ConsentPopup,
  CharacterCount,
  InvalidCharactersError,
} from "~/components";
import { useNavigate, useLocation } from "react-router-dom";
import { useAcid } from "../../hooks/useAcid";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { MorningIcon } from "../../components/Icon/Icons/MorningIcon";
import { AfternoonIcon } from "../../components/Icon/Icons/AfternoonIcon";
import { EveningIcon } from "../../components/Icon/Icons/EveningIcon";
import { AnytimeIcon } from "../../components/Icon/Icons/AnytimeIcon";
import checkIcon from "../../assets/common/checkmark.svg";
import { buildStructuredBeverageData } from "../../utils/beveragePayload";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import {
  DISALLOWED_CHARACTERS_MESSAGE,
  hasDisallowedCharacters,
} from "../../utils/beverageTextValidation";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";
import { beverageCategories } from "./constants/beverage-categories";
import { beveragePreferences } from "./constants/beverage-preferences";
import { beveragePlaceholders } from "./constants/beverage-placeholders";

// Constants
const FOOTER_PROXIMITY_THRESHOLD = 200; // pixels from bottom to trigger footer adjustment
const SCROLL_THROTTLE_DELAY = 100; // milliseconds between scroll checks

const timeOfDayOptions = [
  { name: "Morning", Icon: MorningIcon },
  { name: "Afternoon", Icon: AfternoonIcon },
  { name: "Evening", Icon: EveningIcon },
  { name: "Anytime", Icon: AnytimeIcon },
];
export const Beverage = () => {
  const cid = "beverage";
  const navigate = useNavigate();
  const location = useLocation();
  const { acid } = useAcid();
  const { isResetting, setContentClassName } = useLayoutStore();
  const { setShowPageLoader } = useCampaignPage();
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPreferences, setSelectedPreferences] = useState({});
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState({});
  const [additionalDetails, setAdditionalDetails] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [savedCategories, setSavedCategories] = useState(new Set());
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [isConsentSaving, setIsConsentSaving] = useState(false);
  const [categoryToAutoSave, setCategoryToAutoSave] = useState(null);
  const [savingCategories, setSavingCategories] = useState(new Set());
  const [consentPopupDismissed, setConsentPopupDismissed] = useState(() => {
    // Check sessionStorage on init to see if user already responded to consent
    return sessionStorage.getItem("beverageConsentResponded") === "true";
  });
  // Track the user's consent choice for this session (null = not yet set, true = opted out, false = opted in)
  const [sessionOptedOut, setSessionOptedOut] = useState(() => {
    const stored = sessionStorage.getItem("beverageConsentOptedOut");
    return stored !== null ? stored === "true" : null;
  });
  /**
   * Synchronous locks: refs update immediately, unlike the isModalSaving/
   * isSaving state checks below which only block re-entry after React
   * commits a re-render. Guards against double clicks/taps and effect
   * re-fires that land before that commit happens.
   */
  const saveCategoryLockRef = useRef(false);
  const submitLockRef = useRef(false);
  const consentChoiceLockRef = useRef(false);

  // Send page view analytics event on mount
  useEffect(() => {
    return sendPageViewWhenConsentReady("Beverage Page", 'Passions-FavBevs', 'beverages');
  }, []);

  useEffect(() => {
    if (showSignInDialog) {
      sendAnalyticsPageViewEvent("Beverage Page Modal: Sign In", 'Passions-FavBevs', 'beverages');
    }
  }, [showSignInDialog]);

  useEffect(() => {
    if (showConsentPopup) {
      sendAnalyticsPageViewEvent("Beverage Page Modal: Consent", 'Passions-FavBevs', 'beverages');
    }
  }, [showConsentPopup]);

  // Send analytics event when beverage modal opens
  useEffect(() => {
    if (selectedCategory) {
      sendAnalyticsPageViewEvent(
        `Beverage Page Modal: How do you enjoy your ${selectedCategory.name}?`,
        'Passions-FavBevs',
        'beverages'
      );
    }
  }, [selectedCategory]);


  // Restore beverage selections from sessionStorage on mount (after login redirect)
  useEffect(() => {
    const savedData = sessionStorage.getItem("pendingBeverageSelections");
    if (!savedData) {
      return;
    }

    try {
      const parsed = JSON.parse(savedData);
      const {
        version,
        timestamp,
        sessionId,
        preferences,
        timeOfDay,
        details,
        saved,
        pendingSaveCategory,
      } = parsed;

      // Validate version
      if (version !== 1) {
        console.warn("⚠️ Incompatible beverage selections version:", version);
        sessionStorage.removeItem("pendingBeverageSelections");
        return;
      }

      // Check if data is stale (older than 10 minutes)
      const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
      const age = Date.now() - timestamp;
      if (age > MAX_AGE_MS) {
        console.warn(
          `⚠️ Beverage selections expired (age: ${Math.round(age / 1000)}s)`,
        );
        sessionStorage.removeItem("pendingBeverageSelections");
        return;
      }

      // Validate session ID matches (prevents cross-session pollution)
      const currentSessionId = sessionStorage.getItem("beverageSessionId");
      if (sessionId && currentSessionId && sessionId !== currentSessionId) {
        console.warn("⚠️ Session ID mismatch, ignoring stale selections");
        sessionStorage.removeItem("pendingBeverageSelections");
        return;
      }

      // Validate data structure
      if (!preferences || typeof preferences !== "object") {
        console.warn("⚠️ Invalid beverage selections data structure");
        sessionStorage.removeItem("pendingBeverageSelections");
        return;
      }

      // All validations passed - restore state
      setSelectedPreferences(preferences);
      setSelectedTimeOfDay(timeOfDay || {});
      setAdditionalDetails(details || {});
      setSavedCategories(new Set(saved || []));

      if (pendingSaveCategory) {
        setCategoryToAutoSave(pendingSaveCategory);
      }

      // Clear the saved selections after successful restoration
      sessionStorage.removeItem("pendingBeverageSelections");
      console.log(
        `✅ Restored beverage selections (age: ${Math.round(age / 1000)}s, session: ${sessionId})`,
      );
    } catch (error) {
      console.error("❌ Failed to restore beverage selections:", error);
      sessionStorage.removeItem("pendingBeverageSelections");
    }
  }, []);

  useEffect(() => {
    if (isResetting) {
      return;
    }

    setContentClassName(styles.parentContent);
    console.log(
      "Beverage component mounted, categories:",
      beverageCategories.length,
    );
  }, [isResetting, setContentClassName]);

  // Handle scroll to avoid footer overlap with throttling
  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeout) {
        return;
      }

      const timeout = setTimeout(() => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Calculate if we're near the bottom
        const nearBottom =
          scrollTop + windowHeight >=
          documentHeight - FOOTER_PROXIMITY_THRESHOLD;
        setIsNearFooter(nearBottom);
        setScrollTimeout(null);
      }, SCROLL_THROTTLE_DELAY);

      setScrollTimeout(timeout);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [scrollTimeout]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
  };

  const togglePreference = (category, preference) => {
    setSelectedPreferences((prev) => {
      const categoryPrefs = prev[category] || [];
      const isSelected = categoryPrefs.includes(preference);

      return {
        ...prev,
        [category]: isSelected
          ? categoryPrefs.filter((p) => p !== preference)
          : [...categoryPrefs, preference],
      };
    });
  };

  const toggleTimeOfDay = (timeOfDay) => {
    if (!selectedCategory) return;
    const categoryName = selectedCategory.name;

    setSelectedTimeOfDay((prev) => {
      const categoryTimes = prev[categoryName] || [];
      const isSelected = categoryTimes.includes(timeOfDay);

      let newValue = isSelected
        ? categoryTimes.filter((i) => i !== timeOfDay)
        : [...categoryTimes, timeOfDay];

      // We want to toggle items properly, so we will select anytime OR other options. Statements below are responsible for that
      if (timeOfDay === "Anytime") {
        newValue = newValue.filter((i) => i === timeOfDay);
      } else {
        newValue = newValue.filter((i) => i !== "Anytime");
      }

      if (newValue.length === 3) {
        newValue = ["Anytime"];
      }

      return {
        ...prev,
        [categoryName]: newValue,
      };
    });
  };

  const saveCategoryPreferences = useCallback(
    async (categoryObj, overrideOptedOut) => {
      if (saveCategoryLockRef.current) {
        return false;
      }
      // Check if already saving this category
      if (savingCategories.has(categoryObj.name)) {
        return false;
      }

      if (isModalSaving) {
        return false; // Prevent double-submission
      }

      saveCategoryLockRef.current = true;
      setSavingCategories((prev) => new Set([...prev, categoryObj.name]));
      setIsModalSaving(true);
      setSaveError(null);

      try {
        // Prepare structured data for the current category
        const currentCategoryData = {
          category: categoryObj.name,
          types:
            selectedPreferences[categoryObj.name]?.map((type) => ({
              type: type,
              additionalDetails: additionalDetails[categoryObj.name] || "",
              timeOfDay: selectedTimeOfDay[categoryObj.name] || [],
            })) || [],
        };

        /**
         * overrideOptedOut bypasses the stale sessionOptedOut closure so a
         * just-answered consent choice is saved immediately, instead of
         * waiting for the async state update to land.
         */
        const optedOut =
          overrideOptedOut !== undefined
            ? overrideOptedOut
            : sessionOptedOut !== null
              ? sessionOptedOut
              : false;

        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          {
            acid,
            structuredBeverageData: [currentCategoryData],
            optedOutPersonalization: optedOut,
          },
        );

        // Store preferences in sessionStorage for persistence
        const savedData = buildStructuredBeverageData(
          selectedPreferences,
          additionalDetails,
          selectedTimeOfDay,
        );
        sessionStorage.setItem(
          "savedBeveragePreferences",
          JSON.stringify(savedData),
        );

        // Mark the current category as saved
        setSavedCategories((prev) => new Set([...prev, categoryObj.name]));

        // Only close modal if it's open and matches the saved category
        if (selectedCategory && selectedCategory.name === categoryObj.name) {
          handleCloseModal();
        }
        return true;
      } catch (error) {
        console.error("Error saving beverage preferences:", error);
        setSaveError("Failed to save preferences. Please try again.");
        return false;
      } finally {
        setSavingCategories((prev) => {
          const next = new Set(prev);
          next.delete(categoryObj.name);
          return next;
        });
        setIsModalSaving(false);
        saveCategoryLockRef.current = false;
      }
    },
    [
      acid,
      selectedPreferences,
      additionalDetails,
      selectedTimeOfDay,
      isModalSaving,
      selectedCategory,
      savingCategories,
      sessionOptedOut,
    ],
  );

  const handleSavePreferences = async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    // Show consent popup if user hasn't responded this session
    if (!consentPopupDismissed) {
      setShowConsentPopup(true);
      return;
    }

    if (selectedCategory) {
      await saveCategoryPreferences(selectedCategory);
    }
  };

  /**
   * saveCategoryPreferences isn't stably memoized — its own deps change on
   * every save (manual or auto). This ref lets the effects below call the
   * current version without re-running themselves whenever that identity
   * changes, which would otherwise cause them to re-fire on every manual
   * save too, not just the sign-in-return auto-save case.
   */
  const saveCategoryPreferencesRef = useRef(saveCategoryPreferences);
  useEffect(() => {
    saveCategoryPreferencesRef.current = saveCategoryPreferences;
  });

  /**
   * The consent popup can be triggered either by the modal-open manual save
   * (selectedCategory) or by the sign-in-redirect auto-save (categoryToAutoSave,
   * which never opens the modal). Resolve whichever one is actually pending.
   */
  const resolvePendingCategoryObj = useCallback(() => {
    if (selectedCategory) {
      return selectedCategory;
    }
    if (categoryToAutoSave) {
      return (
        beverageCategories.find((c) => c.name === categoryToAutoSave) || null
      );
    }
    return null;
  }, [selectedCategory, categoryToAutoSave]);

  /**
   * Persists the guest's consent choice by re-running the save for whichever
   * category triggered the popup, this time with the just-answered opted-out
   * value. sessionStorage is only written after that save actually succeeds,
   * so a failed save can't leave the session believing consent was recorded
   * when the backend never received it.
   */
  const handleConsentChoice = useCallback(
    async (optedOut) => {
      if (consentChoiceLockRef.current) {
        return;
      }

      const categoryObj = resolvePendingCategoryObj();
      if (!categoryObj) {
        /**
         * Popup shown with nothing pending to save (shouldn't normally
         * happen) — just record the choice locally for future saves.
         */
        sessionStorage.setItem("beverageConsentResponded", "true");
        sessionStorage.setItem("beverageConsentOptedOut", String(optedOut));
        setSessionOptedOut(optedOut);
        setConsentPopupDismissed(true);
        setShowConsentPopup(false);
        return;
      }

      consentChoiceLockRef.current = true;
      setIsConsentSaving(true);

      try {
        const saved = await saveCategoryPreferencesRef.current(
          categoryObj,
          optedOut,
        );
        if (saved) {
          sessionStorage.setItem("beverageConsentResponded", "true");
          sessionStorage.setItem("beverageConsentOptedOut", String(optedOut));
          setSessionOptedOut(optedOut);
          setConsentPopupDismissed(true);
          setShowConsentPopup(false);
          if (categoryToAutoSave) {
            setCategoryToAutoSave(null);
          }
        }
        /**
         * On failure, leave the popup open (saveCategoryPreferences already
         * set saveError) so the guest can retry instead of the session
         * silently believing consent was recorded.
         */
      } finally {
        setIsConsentSaving(false);
        consentChoiceLockRef.current = false;
      }
    },
    [resolvePendingCategoryObj, categoryToAutoSave],
  );

  // User opts IN to personalization (NOT opted out)
  const handleConsentAccept = useCallback(async () => {
    await handleConsentChoice(false);
  }, [handleConsentChoice]);

  // User opts OUT of personalization
  const handleConsentDecline = useCallback(async () => {
    await handleConsentChoice(true);
  }, [handleConsentChoice]);

  /**
   * Dismissing the dialog without making a choice defaults to opted out.
   * Unlike accept/decline, closing must always work immediately — an outage
   * or slow network shouldn't leave the guest stuck unable to dismiss this
   * dialog. The opted-out value is still persisted best-effort in the
   * background via handleConsentChoice; if that fails, consent stays
   * unanswered so the popup correctly reappears on the next save attempt.
   */
  const handleConsentClose = useCallback(() => {
    setShowConsentPopup(false);
    handleConsentChoice(true);
  }, [handleConsentChoice]);

  /**
   * Auto-save effect: only acid/categoryToAutoSave actually becoming
   * available should trigger this, nothing else.
   */
  useEffect(() => {
    const autoSave = async () => {
      if (acid && categoryToAutoSave) {
        /**
         * Mirror the manual save's consent gate: don't save with a default
         * consent value the guest never confirmed — show the popup and wait
         * for handleConsentChoice to resolve it (categoryToAutoSave stays
         * set so it can find this category again once answered).
         */
        if (!consentPopupDismissed) {
          setShowConsentPopup(true);
          return;
        }

        const categoryObj = beverageCategories.find(
          (c) => c.name === categoryToAutoSave,
        );
        if (categoryObj) {
          console.log(`🔄 Auto-saving pending category: ${categoryToAutoSave}`);
          /**
           * Clear before awaiting: a re-render mid-request can't see a
           * stale truthy categoryToAutoSave and fire a second save.
           */
          setCategoryToAutoSave(null);
          await saveCategoryPreferencesRef.current(categoryObj);
        }
      }
    };

    autoSave();
  }, [acid, categoryToAutoSave, consentPopupDismissed]);

  // Only categories with selected types are submitted (see buildStructuredBeverageData),
  // so text left behind in a fully deselected category must not block saving.
  const hasInvalidSubmittedText = useMemo(
    () =>
      Object.entries(additionalDetails).some(
        ([category, text]) =>
          selectedPreferences[category]?.length > 0 &&
          hasDisallowedCharacters(text),
      ),
    [additionalDetails, selectedPreferences],
  );

  // Drop the invalid-characters error as soon as the offending text is fixed.
  useEffect(() => {
    if (!hasInvalidSubmittedText) {
      setSubmitError((prev) =>
        prev === DISALLOWED_CHARACTERS_MESSAGE ? null : prev,
      );
    }
  }, [hasInvalidSubmittedText]);

  const handleSubmit = useCallback(async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    if (submitLockRef.current) {
      return;
    }
    if (isSaving) {
      return; // Prevent double-submission
    }
    // Every selected category's free text is submitted here, including ones typed but never saved.
    if (hasInvalidSubmittedText) {
      setSubmitError(DISALLOWED_CHARACTERS_MESSAGE);
      return;
    }
    submitLockRef.current = true;

    setIsSaving(true);
    setSubmitError(null);

    try {
      const savedData = buildStructuredBeverageData(
        selectedPreferences,
        additionalDetails,
        selectedTimeOfDay,
      );

      // Use session consent value if set, otherwise fall back to profile store
      const optedOut =
        sessionOptedOut !== null
          ? sessionOptedOut
          : false;

      await actionWebInvoke(
        actions["marriott-passions-v3/save-beverage-preferences"],
        {},
        {
          acid,
          structuredBeverageData: savedData.beverageData,
          optedOutPersonalization: optedOut,
        },
      );

      console.log("Beverage preferences saved successfully");

      // Store preferences in sessionStorage for the thank you page
      sessionStorage.setItem(
        "savedBeveragePreferences",
        JSON.stringify(savedData),
      );

      // Preserve query parameters when navigating
      navigate(`/beverage/thank-you${location.search}`);
    } catch (error) {
      console.error("Error saving beverage preferences:", error);
      setSubmitError("Failed to save your preferences. Please try again.");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }, [
    acid,
    selectedPreferences,
    additionalDetails,
    selectedTimeOfDay,
    isSaving,
    hasInvalidSubmittedText,
    setShowSignInDialog,
    navigate,
    location.search,
    sessionOptedOut,
  ]);

  const onCloseDialog = useCallback(
    () => setShowSignInDialog(false),
    [setShowSignInDialog],
  );

  const onSubmitDialog = useCallback(() => {
    // Generate or retrieve session ID for this beverage selection session
    let sessionId = sessionStorage.getItem("beverageSessionId");
    if (!sessionId) {
      sessionId = `bev-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("beverageSessionId", sessionId);
    }

    // Save current selections to sessionStorage before redirect with versioning
    const selectionsToSave = {
      version: 1, // Schema version for future compatibility
      timestamp: Date.now(), // For expiration checking
      sessionId: sessionId, // Unique ID for this selection session
      preferences: selectedPreferences,
      timeOfDay: selectedTimeOfDay,
      details: additionalDetails,
      saved: Array.from(savedCategories),
      pendingSaveCategory: selectedCategory?.name,
    };

    try {
      sessionStorage.setItem(
        "pendingBeverageSelections",
        JSON.stringify(selectionsToSave),
      );
      console.log(
        `💾 Saved beverage selections (session: ${sessionId}, items: ${Object.keys(selectedPreferences).length})`,
      );
    } catch (error) {
      console.error("❌ Failed to save beverage selections:", error);
      // Continue with login even if save fails
    }

    onCloseDialog();
    setShowPageLoader(true);
  }, [
    onCloseDialog,
    setShowPageLoader,
    selectedPreferences,
    selectedTimeOfDay,
    additionalDetails,
    savedCategories,
    selectedCategory,
  ]);

  // Check if at least one preference has been saved
  const hasSavedPreferences = savedCategories.size > 0;

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.headerTitle}>
            Tap to select your favorite drinks
          </h1>

          {submitError && (
            <div className={styles.errorMessage}>{submitError}</div>
          )}

          {hasSavedPreferences && (
            <div
              className={`${styles.doneButtonContainer} ${isNearFooter ? styles.nearFooter : ""}`}
            >
              <button
                data-testid="beverage-done"
                className={`${styles.doneButton} custom_click_track`}
                data-custom_click_track_value={`Beverage Footer|Done|internal`}
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Done"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.categoryGrid}>
          {beverageCategories.map((category, index) => {
            const isSaved = savedCategories.has(category.name);
            const isSavingCategory = savingCategories.has(category.name);

            return (
              <div
                key={index}
                data-testid="beverage-category"
                className={`${styles.categoryCard} ${isSaved ? styles.categoryCardSelected : ""} custom_click_track`}
                data-custom_click_track_value={`Beverages|Category Selection: ${category.name}|internal`}
                onClick={() => handleCategoryClick(category)}
              >
                <div className={styles.categoryImageWrapper}>
                  <img
                    src={category.image}
                    alt={category.name}
                    className={styles.categoryImage}
                    loading="lazy"
                    onError={(e) => {
                      console.error(
                        `Failed to load image for ${category.name}:`,
                        category.image,
                      );
                      e.target.style.backgroundColor = "#f0f0f0";
                    }}
                  />
                  <div className={styles.categoryOverlay}>
                    <span className={styles.categoryName}>{category.name}</span>
                    {isSavingCategory && (
                      <div className={styles.savingBadge}>
                        <span>Saving...</span>
                      </div>
                    )}
                    {!isSavingCategory && isSaved && (
                      <>
                        <div className={styles.addedBadge} data-testid="beverage-category-added">
                          <span>Added</span>
                          <img
                            src={checkIcon}
                            alt="Check"
                            className={styles.addedBadgeIcon}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedCategory && !showSignInDialog && !showConsentPopup && (
        <DialogUi
          data-testid="beverage-details-dialog"
          onClose={handleCloseModal}
          disableOverlayClose={true}
          header={null}
          body={
            <div className={styles.beverageModal}>
              <button
                data-testid="beverage-details-close"
                className={`${styles.closeButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Close Category Modal: ${selectedCategory.name}|internal`}
                onClick={handleCloseModal}
                aria-label={`Close ${selectedCategory.name} preferences`}
              >
                ✕
              </button>
              <h2 className={styles.modalQuestion}>
                How do you enjoy your{" "}
                {selectedCategory.name.substring(0, 1).toUpperCase() +
                  selectedCategory.name.substring(1)}
                ?
              </h2>

              {beveragePreferences[selectedCategory.name]?.length > 0 && (
                <div className={styles.preferencesSection}>
                  <h3 className={styles.sectionLabel}>Type</h3>
                  <div className={styles.optionsGrid}>
                    {beveragePreferences[selectedCategory.name].map(
                      (preference, index) => {
                        const isSelected =
                          selectedPreferences[selectedCategory.name]?.includes(
                            preference,
                          );
                        return (
                          <button
                            key={index}
                            data-testid="beverage-type-option"
                            aria-pressed={Boolean(isSelected)}
                            className={`${styles.optionButton} ${isSelected ? styles.optionButtonSelected : ""} custom_click_track`}
                            data-custom_click_track_value={`Beverages|Selected Category: ${selectedCategory.name}, Preference Selection: ${preference}|internal`}
                            onClick={() =>
                              togglePreference(
                                selectedCategory.name,
                                preference,
                              )
                            }
                          >
                            {preference}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
              )}

              <div
                className={`${styles.additionalSectionsContainer} ${
                  selectedPreferences[selectedCategory.name]?.length > 0
                    ? styles.additionalSectionsVisible
                    : styles.additionalSectionsHidden
                }`}
              >
                {selectedPreferences[selectedCategory.name]?.length ? (
                  <div className={styles.typeSpecificSection}>
                    <div className={styles.preferencesSection}>
                      <h4 className={styles.sectionLabel}>
                        Tell us more (optional)
                      </h4>
                      <textarea
                        data-testid="beverage-details-input"
                        className={styles.additionalDetailsInput}
                        placeholder={
                          beveragePlaceholders[selectedCategory.name] ||
                          `Tell us more about your ${selectedCategory.name.toLowerCase()} preferences...`
                        }
                        value={additionalDetails[selectedCategory.name] || ""}
                        onChange={(e) =>
                          setAdditionalDetails((prev) => ({
                            ...prev,
                            [selectedCategory.name]: e.target.value,
                          }))
                        }
                        maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                        rows={2}
                      />
                      <CharacterCount
                        value={additionalDetails[selectedCategory.name]}
                        max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                      />
                      <InvalidCharactersError
                        value={additionalDetails[selectedCategory.name]}
                      />
                    </div>

                    <p className={styles.hint}>
                      Please do not include profanity, unintelligible
                      information, personally identifiable information, or
                      offensive or inappropriate content.
                    </p>

                    <div className={styles.preferencesSection}>
                      <h4 className={styles.sectionLabel}>
                        Time of day (optional)
                      </h4>
                      <div className={styles.timeOfDayGrid}>
                        {timeOfDayOptions.map(({ name, Icon }) => {
                          const isSelected =
                            selectedTimeOfDay[selectedCategory.name]?.includes(
                              name,
                            );
                          return (
                            <button
                              key={name}
                              data-testid="beverage-time-option"
                              aria-pressed={Boolean(isSelected)}
                              className={`${styles.timeOfDayButton} ${isSelected ? styles.timeOfDayButtonSelected : ""} custom_click_track`}
                              data-custom_click_track_value={`Beverages|Selected Category: ${selectedCategory.name}, Time Of Day Selection: ${name}|internal`}
                              onClick={() => toggleTimeOfDay(name)}
                            >
                              <Icon
                                alt={name}
                                className={styles.timeOfDayIcon}
                              />
                              <span className={styles.timeOfDayLabel}>
                                {name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {saveError && (
                <div className={styles.errorMessage}>{saveError}</div>
              )}

              <button
                data-testid="beverage-details-save"
                className={`${styles.saveButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Save Category Preferences: ${selectedCategory.name}|internal`}
                onClick={handleSavePreferences}
                disabled={
                  isModalSaving ||
                  !selectedPreferences[selectedCategory.name]?.length ||
                  hasDisallowedCharacters(additionalDetails[selectedCategory.name])
                }
              >
                {isModalSaving ? "Saving..." : "Save"}
              </button>

              <p className={styles.privacyText}>
                Preferences can be updated at any time in my{" "}
                <a
                  href="https://www.marriott.com/loyalty/myAccount/default.mi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.privacyLink} custom_click_track`}
                  data-custom_click_track_value="Beverages|Bonvoy Account Profile Link|external"
                >
                  Bonvoy Account Profile
                </a>
                , and I can learn more about how my data is collected and used
                in the{" "}
                <a
                  href="https://www.marriott.com/about/privacy.mi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.privacyLink} custom_click_track`}
                  data-custom_click_track_value="Beverages|Global Privacy Statement Link|external"
                >
                  Global Privacy Statement
                </a>
                .
              </p>
            </div>
          }
          footer={null}
          modalStyle={styles.customModalDialog}
          modalBodyStyle={styles.customModalBody}
          modalHeaderStyle={styles.hideDefaultHeader}
          closeButtonStyle={styles.hideDefaultCloseButton}
        />
      )}
      {showSignInDialog && (
        <SignInDialogUi
          onClose={onCloseDialog}
          onSubmit={onSubmitDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage/preferences${stripAcidFromSearch(location.search)}`}
        />
      )}
      {showConsentPopup && (
        <ConsentPopup
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
          onClose={handleConsentClose}
          loading={isConsentSaving}
        />
      )}
    </>
  );
};
